<script setup lang="ts">
import type { z } from 'zod';
import type { historySchema } from '@atum-remote/nyx-link-protocol';
import { formatRelativeTime, formatTimestamp } from '../lib/format.js';
import AppIcon from './AppIcon.vue';

type Session = z.infer<typeof historySchema>['sessions'][number];

/** Captured IDE and CLI sessions for one project, read from Nyx cloud history. */
defineProps<{
  projects: readonly { projectKey: string; name: string }[];
  sessions: readonly Session[];
  selectedId: string | null;
  loaded: boolean;
  nextCursor: string | null;
  paged: boolean;
  cloudAvailable: boolean;
  busy: boolean;
  connected: boolean;
}>();
const emit = defineEmits<{ load: []; more: []; select: [session: Session] }>();
const project = defineModel<string>('project', { required: true });
</script>

<template>
  <div class="nyx-list" role="group" aria-label="Captured sessions">
    <span class="select-field nyx-list-project">
      <select v-model="project" aria-label="History project" :disabled="busy || !projects.length">
        <option v-if="!projects.length" value="">No projects</option>
        <option v-for="entry in projects" :key="entry.projectKey" :value="entry.projectKey">
          {{ entry.name }}
        </option>
      </select>
      <AppIcon name="chevronDown" />
    </span>
    <p v-if="!cloudAvailable" class="nyx-list-empty">
      Nyx cloud history is unavailable on this host.
    </p>
    <template v-else>
      <button
        v-if="!loaded"
        type="button"
        class="small"
        :disabled="busy || !connected || !project"
        @click="emit('load')"
      >
        <AppIcon name="clock" />Load captured sessions
      </button>
      <p v-else-if="!sessions.length" class="nyx-list-empty">
        No captured sessions for this project yet.
      </p>
      <button
        v-for="session in sessions"
        :key="session.sessionId"
        type="button"
        class="nyx-task"
        :aria-current="session.sessionId === selectedId ? 'true' : undefined"
        :disabled="busy"
        @click="emit('select', session)"
      >
        <span class="nyx-task-top">
          <strong class="nyx-task-name">{{ session.title ?? session.sessionId }}</strong>
          <time
            class="nyx-task-time"
            :datetime="session.updatedAt"
            :title="formatTimestamp(session.updatedAt)"
            >{{ formatRelativeTime(session.updatedAt) }}</time
          >
        </span>
        <span class="nyx-task-meta">
          <span class="nyx-task-client">{{ session.client }}</span>
          <span class="nyx-task-model mono">{{ session.status }}</span>
          <span v-if="session.workItems.length" class="nyx-task-model mono">{{
            session.workItems.join(', ')
          }}</span>
        </span>
      </button>
      <button
        v-if="loaded && nextCursor"
        type="button"
        class="small quiet"
        :disabled="busy"
        @click="emit('more')"
      >
        More history
      </button>
      <p v-if="loaded && !paged" class="hint">This Nyx server does not page history.</p>
    </template>
  </div>
</template>
